// Backend API wrapper for secure AI calls
// All AI API calls now go through backend to protect API keys
//
// API_BASE_URL is resolved at runtime from the browser's current hostname so
// that LAN access via bare hostname OR .local both work automatically without
// any hardcoded IP address.

function getApiBaseUrl() {
  if (typeof window === 'undefined') {
    // SSR / test environment fallback
    return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  }

  const host = window.location.hostname;

  // Production domain → always use the production backend
  if (host.includes('fastgrader.org')) {
    return 'https://backend.fastgrader.org/api';
  }

  // LAN / local dev → mirror whatever hostname the browser used to open the
  // app (e.g. fast107, fast107.local, or localhost).  This means a PC
  // that opens http://fast107:3000 automatically calls fast107:5000/api.
  return `http://${host}:5000/api`;
}

const API_BASE_URL = getApiBaseUrl();

/** Fallback when this browser has no saved provider. Teacher rubrics do not use this. */
export function getDefaultApiProvider() {
  return "local";
}

/**
 * Provider saved by a page dropdown (gemini, openai, local, deepseek, minimax).
 * Teacher rubric generation ignores this and uses the examiner's saved model.
 */
export function getApiProvider() {
  return localStorage.getItem("apiProvider") || getDefaultApiProvider();
}

/**
 * Get auth token from localStorage
 */
function getAuthToken() {
  return localStorage.getItem('authToken');
}

/**
 * Generate rubric from text using backend AI endpoint
 * @param {Object} params - { sourceText, teacherInstruction }
 * @returns {Promise<string>} - Generated rubric text
 */
export async function generateRubricFromText({ sourceText, teacherInstruction }) {
  const trimmed = (sourceText || "").trim();
  if (!trimmed) {
    throw new Error("No exam text provided for rubric generation.");
  }

  const provider = getApiProvider();
  const token = getAuthToken();

  if (!token) {
    throw new Error("Authentication required. Please login again.");
  }

  try {
    const response = await fetch(`${API_BASE_URL}/ai/generate-rubric`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        sourceText: trimmed.slice(0, 12000),
        teacherInstruction: (teacherInstruction || "").trim(),
        provider
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to generate rubric');
    }

    if (!data.rubric) {
      throw new Error('AI did not return any text. Please try again.');
    }

    return data.rubric;
  } catch (error) {
    console.error('Generate rubric error:', error);
    throw error;
  }
}

/**
 * Chat with AI for rubric generation using backend endpoint
 * @param {string} conversationHistory - Full conversation text
 * @param {string} fileText - Optional file content
 * @param {Object} extras - { subjectName, subjectRules, subjectInstructions, mode }
 *   mode: 'rubric' (default) | 'concept_check' | 'corrected_paper' | 'paper_diff'
 *   Non-'rubric' modes tell the backend to skip the rubric-shaping system
 *   prompt so the user prompt drives the output format.
 * @returns {Promise<string>} - AI response
 */
export async function chatWithRubricAI(conversationHistory, fileText = "", extras = {}) {
  const provider = getApiProvider();
  const token = getAuthToken();

  if (!token) {
    throw new Error("Authentication required. Please login again.");
  }

  // Optional per-subject marking rules — when present the backend prepends a
  // user-override block to the system prompt so the AI follows these exact
  // marks/rule pairings instead of the defaults. subjectInstructions is a
  // free-form string of additional rubric-shaping instructions for the AI.
  const subjectName = (extras && extras.subjectName) || '';
  const subjectRules = (extras && Array.isArray(extras.subjectRules)) ? extras.subjectRules : [];
  const subjectInstructions = (extras && typeof extras.subjectInstructions === 'string')
    ? extras.subjectInstructions
    : '';
  const mode = (extras && typeof extras.mode === 'string') ? extras.mode : 'rubric';
  const rubricModel = (extras && typeof extras.rubricModel === 'string') ? extras.rubricModel : '';
  const rubricHost = (extras && typeof extras.rubricHost === 'string') ? extras.rubricHost : '';
  const onQueue = extras && typeof extras.onQueue === 'function' ? extras.onQueue : null;
  const queueToken = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `q${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

  let queuePoll = null;
  if (onQueue) onQueue(null);
  const watchesGpu = provider === 'local'
    || rubricHost === 'gpu'
    || String(rubricModel || '').startsWith('local:');
  if (onQueue && watchesGpu) {
    const pullQueue = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/ai/gpu-queue/${queueToken}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data && (data.status === 'queued' || data.status === 'running')) onQueue(data);
      } catch (_) { /* banner stays on the last status */ }
    };
    queuePoll = setInterval(pullQueue, 1000);
    pullQueue();
  }

  try {
    const response = await fetch(`${API_BASE_URL}/ai/chat-rubric`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        conversationHistory,
        fileText,
        provider,
        subjectName,
        subjectRules,
        subjectInstructions,
        mode,
        queue_token: queueToken,
        ...(rubricModel ? { rubric_model: rubricModel } : {}),
        ...(rubricHost ? { rubric_host: rubricHost } : {}),
        ...(extras && extras.logAbout ? { logAbout: extras.logAbout } : {}),
        ...(extras && extras.logHint ? { logHint: extras.logHint } : {})
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to chat with AI');
    }

    if (!data.content) {
      throw new Error('AI returned empty content. Check console logs.');
    }

    return { content: data.content, model: data.model || '' };
  } catch (error) {
    console.error('Chat rubric error:', error);
    throw error;
  } finally {
    if (queuePoll) clearInterval(queuePoll);
  }
}

/**
 * Legacy function name for backward compatibility
 * This is used by RubricUploader component
 */
export { generateRubricFromText as generateRubricFromTextBackend };
